/**
 * A location's facilities contacts (Warren, 28 Oct 2026, item 6): the people
 * emailed when a charger there goes offline, comes back, or keeps refusing
 * starts. Name and email only, never a phone number. Editable in Admin by EVI
 * staff and by the site owner's team within their own scope, and seeded by
 * script from what EVI already holds.
 *
 * One rule set for the API (PUT /locations/:id/facilities-contacts and the
 * seed import) and for the Admin form, so the form refuses exactly what the
 * server refuses. Pure: no ODM, no I/O.
 *
 * WHY A NAME CANNOT CARRY A PHONE NUMBER. The name is the greeting of every
 * alert email ("Hello Nick,"), and no alert email may carry a phone number.
 * A name with one is refused rather than stripped, so the person editing
 * learns why.
 */
/** Most contacts one location holds. */
export const FACILITIES_CONTACTS_MAX = 20;
export const FACILITIES_CONTACT_NAME_MAX = 100;
export const FACILITIES_CONTACT_EMAIL_MAX = 254;
/** Where a contact came from. 'admin' for anyone typing it into Admin; the
 *  seed and import scripts write their own labels (for example
 *  'seed:site_owner', 'import:sheet'). */
export const FACILITIES_CONTACT_SOURCE_ADMIN = 'admin';
const SOURCE_PATTERN = /^[a-z0-9_:.-]{1,60}$/;
/** One address, no display name, no spaces. The local part has no leading,
 *  trailing or doubled dot; the domain is dot-separated labels of letters,
 *  digits and hyphens (never starting or ending with a hyphen) ending in a
 *  top-level domain of letters. Deliberately plain: the mail transport is the
 *  real judge, this stops the typos that would store a contact who then
 *  never gets an alert (joey@greatlakeshg..com, joey@greatlakeshg.com.). */
const EMAIL_PATTERN = /^[^\s@<>()[\],;:".]+(?:\.[^\s@<>()[\],;:".]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;
/** Seven or more digits in a row, allowing the usual separators: a phone
 *  number in every format the emails could otherwise carry. */
export const PHONE_NUMBER_PATTERN = /(?:\+?\d[\s().-]*){7,}/;
/**
 * What each contact is to the site (Warren, 28 Oct 2026: "Each location
 * contact gets a role: Property manager, Concierge, Facilities, Owner,
 * Other"). Every stored contact carries one; a contact stored before roles
 * existed reads as the role its source implies (contactRoleOf).
 */
export const FACILITIES_CONTACT_ROLES = ['property_manager', 'concierge', 'facilities', 'owner', 'other'];
export const FACILITIES_CONTACT_ROLE_LABELS = {
    property_manager: 'Property manager',
    concierge: 'Concierge',
    facilities: 'Facilities',
    owner: 'Owner',
    other: 'Other',
};
export function isFacilitiesContactRole(value) {
    return typeof value === 'string' && FACILITIES_CONTACT_ROLES.includes(value);
}
/** A role as typed in the sheet or sent by a script: the id or the label, any
 *  case, spaces or underscores. Null when it is neither. */
export function parseFacilitiesContactRole(value) {
    if (typeof value !== 'string')
        return null;
    const key = value.trim().toLowerCase().replace(/[\s-]+/g, '_');
    if (isFacilitiesContactRole(key))
        return key;
    if (key === 'pm')
        return 'property_manager';
    return null;
}
/**
 * The role a stored label implies, for a contact saved without one: the seed
 * names the account it came from (a Charger Owner grant, the host, the payee
 * account: owner; a Property Manager or Concierge grant; a Building Admin or
 * other Site grant: facilities). Everything else, Admin edits included, is
 * other.
 */
export function roleFromSource(source) {
    const s = (source ?? '').toLowerCase();
    if (/^seed:(site_owner|v1_host|site_hosts_json|stripe_location|stripe_host|stripe_owner)$/.test(s))
        return 'owner';
    if (s === 'seed:property_manager')
        return 'property_manager';
    if (s === 'seed:concierge')
        return 'concierge';
    if (s === 'seed:site_team')
        return 'facilities';
    return 'other';
}
/**
 * The roles an invite from the location page can carry (Warren, 28 Oct 2026,
 * item 5: "email, name, role: Property manager, Concierge, Facilities,
 * Other"). Owner is not one: the site owner is the one inviting.
 */
export const BUILDING_STAFF_INVITE_ROLES = ['property_manager', 'concierge', 'facilities', 'other'];
export function isBuildingStaffInviteRole(value) {
    return typeof value === 'string' && BUILDING_STAFF_INVITE_ROLES.includes(value);
}
/** How long an invite link works: "the link expires in 7 days". */
export const BUILDING_STAFF_INVITE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/** The label a contact added by accepting an invite is stored with. */
export const FACILITIES_CONTACT_SOURCE_INVITE = 'invite:building_staff';
/** A stored contact's role: its own when valid, else what its source implies. */
export function contactRoleOf(contact) {
    return isFacilitiesContactRole(contact.role) ? contact.role : roleFromSource(contact.source);
}
/**
 * How often each contact hears about outages (Warren, 28 Oct 2026,
 * predictive maintenance stage 4): "Right away, Daily digest at 8 am local,
 * Only if down more than 1, 4 or 24 hours, or Off. Default: right away for
 * Down and Site down, weekly for Unstable." Chosen in the location's
 * settings and from the link in every alert email. The "this charger keeps
 * losing its connection" notice is weekly at most for everyone but Off.
 *
 * QUIET BY DEFAULT (Warren, 29 Oct 2026: "Alert emails are too many. Make
 * them quiet by default, configurable in Admin"). 'standard' is the default
 * since then, and a contact who never chose reads as it: the 8 am local
 * digest, plus a live email only when one charger is down longer than the
 * down threshold (2 hours) or a whole site (two or more active chargers)
 * longer than the site threshold (30 minutes), and a back-online email only
 * after a down email. The thresholds and the digest time are Settings,
 * Alerts values (alertSettings.ts), so the wait for 'standard' lives in the
 * Api, not here. A contact who keeps right away or 1, 4 or 24 hours keeps
 * their own emails as before.
 */
export const ALERT_DELIVERY_CHOICES = ['weekly_digest', 'standard', 'right_away', 'daily_digest', 'after_1h', 'after_4h', 'after_24h', 'off'];
export const DEFAULT_ALERT_DELIVERY = 'weekly_digest';
export const ALERT_DELIVERY_LABELS = {
    weekly_digest: 'Weekly overview on Monday (default)',
    standard: 'Standard: a morning summary, plus an email only for long outages',
    right_away: 'Right away',
    daily_digest: 'Daily digest at 8 am',
    after_1h: 'Only if down more than 1 hour',
    after_4h: 'Only if down more than 4 hours',
    after_24h: 'Only if down more than 24 hours',
    off: 'Off',
};
export function isAlertDelivery(value) {
    return typeof value === 'string' && ALERT_DELIVERY_CHOICES.includes(value);
}
/** A contact's choice; Standard when it never chose (or holds a value no longer offered). */
export function deliveryOf(contact) {
    return isAlertDelivery(contact?.alert_delivery) ? contact.alert_delivery : DEFAULT_ALERT_DELIVERY;
}
/** How long an outage must last before this choice hears about it: 0 for
 *  right away, the hours for the "only if down more than" choices, null for
 *  the daily digest and off (no single outage email at all) and for
 *  Standard, whose waits are Settings values the Api reads
 *  (offlineAlerts/delivery.ts). */
export function deliveryDelayMs(delivery) {
    switch (delivery) {
        case 'right_away': return 0;
        case 'after_1h': return 60 * 60_000;
        case 'after_4h': return 4 * 60 * 60_000;
        case 'after_24h': return 24 * 60 * 60_000;
        default: return null;
    }
}
function isoOr(value, fallback) {
    const d = value ? new Date(value) : null;
    return d && !Number.isNaN(d.getTime()) ? d.toISOString() : fallback.toISOString();
}
export function isValidContactEmail(value) {
    return value.length <= FACILITIES_CONTACT_EMAIL_MAX && EMAIL_PATTERN.test(value);
}
/** Case-insensitive identity of an address, for dedupe and matching. */
export function contactEmailKey(email) {
    return email.trim().toLowerCase();
}
/**
 * Validate and normalize a replacement list.
 *
 *   - each entry needs a valid email; the name is optional, trimmed, at most
 *     FACILITIES_CONTACT_NAME_MAX characters, and never holds a phone number;
 *   - the same address twice (any case) is one contact: the first wins;
 *   - at most FACILITIES_CONTACTS_MAX after dedupe;
 *   - a role, when sent, is one of FACILITIES_CONTACT_ROLES; when not sent, a
 *     contact already on the list keeps its role (contactRoleOf) and a new
 *     one takes the role its source implies (roleFromSource);
 *   - a delivery choice, when sent, is one of ALERT_DELIVERY_CHOICES; when
 *     not sent, a contact keeps its own and a new one is Standard;
 *   - a contact already on the list keeps its source, added_by and added_at
 *     (an edit of the name or role does not make it new); a new one gets
 *     `source` (the input's own when `allowSource`, else 'admin'), the actor
 *     and now.
 *
 * Returns the stored list plus which addresses were added and removed, for
 * the audit row.
 */
export function normalizeFacilitiesContacts(input, opts) {
    if (!Array.isArray(input))
        return { ok: false, error: 'Send the contacts as a list.' };
    const existingByKey = new Map((opts.existing ?? []).map((c) => [contactEmailKey(c.email), c]));
    const seen = new Set();
    const contacts = [];
    const fallbackSource = opts.defaultSource ?? FACILITIES_CONTACT_SOURCE_ADMIN;
    for (let i = 0; i < input.length; i += 1) {
        const raw = (input[i] ?? {});
        const email = typeof raw.email === 'string' ? raw.email.trim() : '';
        const name = typeof raw.name === 'string' ? raw.name.trim().replace(/\s+/g, ' ') : '';
        if (!email)
            return { ok: false, error: `Contact ${i + 1} needs an email address.`, index: i };
        if (!isValidContactEmail(email))
            return { ok: false, error: `Contact ${i + 1}: "${email}" is not an email address.`, index: i };
        if (name.length > FACILITIES_CONTACT_NAME_MAX) {
            return { ok: false, error: `Contact ${i + 1}: the name is longer than ${FACILITIES_CONTACT_NAME_MAX} characters.`, index: i };
        }
        if (PHONE_NUMBER_PATTERN.test(name)) {
            return { ok: false, error: `Contact ${i + 1}: a name cannot hold a phone number. Alert emails never carry one.`, index: i };
        }
        const sentRole = raw.role === undefined || raw.role === null || raw.role === '' ? null : raw.role;
        const sentDelivery = raw.alert_delivery === undefined || raw.alert_delivery === null || raw.alert_delivery === '' ? null : raw.alert_delivery;
        if (sentDelivery !== null && !isAlertDelivery(sentDelivery)) {
            return { ok: false, error: `Contact ${i + 1}: choose how often they hear about outages.`, index: i };
        }
        if (sentRole !== null && !isFacilitiesContactRole(sentRole)) {
            return { ok: false, error: `Contact ${i + 1}: choose a role (${FACILITIES_CONTACT_ROLES.map((r) => FACILITIES_CONTACT_ROLE_LABELS[r]).join(', ')}).`, index: i };
        }
        const key = contactEmailKey(email);
        if (seen.has(key))
            continue;
        seen.add(key);
        const before = existingByKey.get(key);
        let source = fallbackSource;
        if (opts.allowSource && typeof raw.source === 'string' && raw.source)
            source = raw.source;
        if (!SOURCE_PATTERN.test(source))
            return { ok: false, error: `Contact ${i + 1}: unknown source "${source}".`, index: i };
        contacts.push(before
            ? {
                name,
                email,
                role: sentRole ?? contactRoleOf(before),
                alert_delivery: sentDelivery ?? deliveryOf(before),
                source: before.source && SOURCE_PATTERN.test(before.source) ? before.source : source,
                added_by: before.added_by ? String(before.added_by) : null,
                added_at: isoOr(before.added_at, opts.now),
            }
            : {
                name, email, role: sentRole ?? roleFromSource(source), alert_delivery: sentDelivery ?? DEFAULT_ALERT_DELIVERY,
                source, added_by: opts.actorId, added_at: opts.now.toISOString(),
            });
    }
    if (contacts.length > FACILITIES_CONTACTS_MAX) {
        return { ok: false, error: `A location holds at most ${FACILITIES_CONTACTS_MAX} facilities contacts.` };
    }
    const afterKeys = new Set(contacts.map((c) => contactEmailKey(c.email)));
    const added = contacts.filter((c) => !existingByKey.has(contactEmailKey(c.email))).map((c) => c.email);
    const removed = [...existingByKey.values()].filter((c) => !afterKeys.has(contactEmailKey(c.email))).map((c) => c.email);
    return { ok: true, contacts, added, removed };
}
//# sourceMappingURL=facilitiesContacts.js.map